import React, {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  UIEvent,
  useCallback,
} from 'react';
import {
  Select,
  InputLabel,
  FormControl,
  Typography,
  MenuItem,
  Checkbox,
  Box,
  Divider,
  InputAdornment,
  FormControlProps,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {t} from 'i18next';
import {ReactComponent as CheckedIcon} from '../../../assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../assets/images/icon-unchecked.svg';
import {ReactComponent as SearchIcon} from '../../../assets/images/icon-search.svg';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import {ReactComponent as CaretUp} from '../../../assets/images/caret-up.svg';
import {ISelectItem} from '../../../shared/types';
import ArrSelectedItemsPanel from './ArrMultiSelectItemPanel';
import ArrTextField from '../arr-text-field/ArrTextField';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import './ArrSelectDropdown.scss';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';
import {
  DEBOUNCE_SEARCH_TIME_OUT,
  POSITION_HEIGHT,
  SCROLL_THRESHOLD,
} from '../../../shared/constants';

const {
  AVAILABLE_SPACE,
  RESERVED_HEIGHT_SEARCH_ROW,
  AVAILABLE_HEIGHT_FACTOR,
  MAX_HEIGHT_MENU,
  DEFAULT_MAX_HEIGHT,
} = POSITION_HEIGHT;
interface BaseProps {
  dropdownLabel: string;
  dropdownItems: ISelectItem[];
  dropdownItemKey?: string;
  dropdownItemDisplay?: string;
  disabled?: boolean;
  error?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  onClearAll?: () => void;
  onApply?: (value: ISelectItem[]) => void;
  sizeVariant?: 'small' | 'medium' | 'large';
  formControlRestProps?: Omit<FormControlProps, 'children'>;
  customWidth?: string | number;
  withSelectAll?: boolean;
  dataTestId?: string;
  checkboxDataTestId?: string;
  customDisplayLabel?: string;
  /** Lazy loading props */
  /** onLoadMore will be called when scrolling near bottom; optional search string will be passed */
  onLoadMore?: (search?: string) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** Trigger API on open. optional search string will be passed */
  onDropdownOpen?: (search?: string) => void;
  shouldLoadOnOpen?: boolean;
}

export interface ArrMultiSelectProps extends BaseProps {
  multiple: true;
  selectedValues: ISelectItem[];
  onSelectionChange: (value: ISelectItem[]) => void;
  noResultText?: string;
  onDropdownToggle?: (isOpen: boolean) => void;
  useDataTestIdsSimilarToArrMultiSelect?: boolean;
}

const ArrMultiSelectLazyLoadDropdown: React.FC<ArrMultiSelectProps> = ({
  dropdownLabel,
  dropdownItems,
  dropdownItemKey = 'id',
  dropdownItemDisplay = 'name',
  selectedValues,
  onSelectionChange,
  disabled = false,
  error = false,
  searchable = false,

  customWidth,

  dataTestId = 'arr-multi-dropdown-form-control',
  checkboxDataTestId = 'arr-multi-lazy-dropdown',
  customDisplayLabel,
  onDropdownOpen,
  shouldLoadOnOpen,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  sizeVariant = 'large',
  formControlRestProps,
  noResultText = '',
  onDropdownToggle,
  useDataTestIdsSimilarToArrMultiSelect,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<ISelectItem[]>(selectedValues);
  const [searchFieldText, setSearchFieldText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [menuWidth, setMenuWidth] = useState<number>();
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(500);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const formControlRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  /** Handle dynamic width and position */
  useEffect(() => {
    if (!customWidth && formControlRef.current) {
      setMenuWidth(formControlRef.current.offsetWidth);
    }
  }, [customWidth, isDropdownOpen]);

  useEffect(() => {
    setSelectedItem(selectedValues);
  }, [selectedValues]);

  // debounce searchFieldText
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchFieldText.trim());
    }, DEBOUNCE_SEARCH_TIME_OUT);
    return () => clearTimeout(id);
  }, [searchFieldText]);

  // call onDropdownOpen when dropdown opens or when debouncedSearch changes while open
  useEffect(() => {
    if (isDropdownOpen && shouldLoadOnOpen) {
      onDropdownOpen?.(debouncedSearch);
    }
  }, [isDropdownOpen, debouncedSearch, shouldLoadOnOpen]);

  // Dynamic position & height calculation
  useEffect(() => {
    const updateMenuPositionAndHeight = () => {
      if (!formControlRef.current) return;

      const rect = formControlRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide dropdown direction
      const position =
        spaceBelow < AVAILABLE_SPACE && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom';
      setMenuPosition(position);

      // Reserve space: search (50) + select all row (50)
      const reservedHeight =
        (searchable ? RESERVED_HEIGHT_SEARCH_ROW : 0) +
        RESERVED_HEIGHT_SEARCH_ROW;

      const availableHeight = position === 'bottom' ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(
        MAX_HEIGHT_MENU,
        availableHeight * AVAILABLE_HEIGHT_FACTOR - reservedHeight
      );
      setMenuMaxHeight(maxHeight > 0 ? maxHeight : DEFAULT_MAX_HEIGHT); // fallback minimal height
    };

    updateMenuPositionAndHeight();
    window.addEventListener('resize', updateMenuPositionAndHeight);
    window.addEventListener('scroll', updateMenuPositionAndHeight, true);

    return () => {
      window.removeEventListener('resize', updateMenuPositionAndHeight);
      window.removeEventListener('scroll', updateMenuPositionAndHeight, true);
    };
  }, [isDropdownOpen, searchable]);

  /** Scroll event for lazy load */
  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
      if (nearBottom && hasMore && !isLoadingMore) {
        onLoadMore?.(debouncedSearch);
      }
    },
    [onLoadMore, hasMore, isLoadingMore, debouncedSearch]
  );

  /** Item selection */
  const handleItemClick = (item: ISelectItem) => {
    const exists = selectedItem.some(
      (s) => s[dropdownItemKey] === item[dropdownItemKey]
    );
    const newSelection = exists
      ? selectedItem.filter((s) => s[dropdownItemKey] !== item[dropdownItemKey])
      : [...selectedItem, item];

    setSelectedItem(newSelection);
    onSelectionChange(newSelection);
  };

  /** Filter items by search (local filter as fallback) */
  const filteredItems = dropdownItems.filter((item) => {
    if (!searchable || !debouncedSearch) return true;
    const value = String(item[dropdownItemDisplay] ?? '').toLowerCase();
    return value.includes(debouncedSearch.toLowerCase());
  });

  const normalizeWidth = (val?: string | number) =>
    typeof val === 'number' ? `${val}rem` : val;
  const dynamicWidth = customWidth ? normalizeWidth(customWidth) : undefined;

  return (
    <FormControl
      variant="filled"
      ref={formControlRef}
      disabled={disabled}
      error={error}
      className={`arr-dropdown-container arr-dropdown-container--${sizeVariant}`}
      fullWidth={!dynamicWidth}
      style={
        dynamicWidth
          ? {
              width: dynamicWidth,
              minWidth: dynamicWidth,
              maxWidth: dynamicWidth,
              flex: '0 0 auto',
            }
          : {
              flex: '1 1 auto',
            }
      }
      data-testid={
        useDataTestIdsSimilarToArrMultiSelect
          ? `wrapper-${dataTestId}`
          : `${dataTestId}-wrapper`
      }
      {...formControlRestProps}
    >
      <InputLabel id={`${dropdownLabel}-label`} className=" arr-dropdown-label">
        {dropdownLabel}
      </InputLabel>
      <Select
        open={isDropdownOpen}
        onOpen={() => {
          setIsDropdownOpen(true);
          onDropdownToggle?.(true);
          // focus search input after menu renders
          requestAnimationFrame(() => {
            if (searchable) searchInputRef.current?.focus();
            if (shouldLoadOnOpen && onDropdownOpen) {
              onDropdownOpen?.(debouncedSearch);
            }
          });
        }}
        onClose={() => {
          setIsDropdownOpen(false);
          onDropdownToggle?.(false);
        }}
        multiple
        labelId={`${dropdownLabel}-label`}
        value={selectedItem}
        SelectDisplayProps={{'data-testid': `${dataTestId}`}}
        renderValue={(selected) => (
          <ArrSelectedItemsPanel
            selectedItems={selected as ISelectItem[]}
            itemDisplay={dropdownItemDisplay}
            disabled={disabled}
            sizeVariant={sizeVariant}
            customDisplayLabel={customDisplayLabel}
            dataTestId={dropdownLabel.replaceAll(' ', '').toLowerCase()}
          />
        )}
        IconComponent={isDropdownOpen ? CaretUp : CaretDown}
        className={`arr-dropdown-container__arr-dropdown-select arr-dropdown-container__arr-dropdown-select--${sizeVariant}`}
        MenuProps={{
          PaperProps: {
            className: `arr-dropdown-container__arr-dropdown-menu arr-dropdown-container__arr-dropdown-menu--${menuPosition} arr-dropdown-container__arr-dropdown-menu--${sizeVariant}`,
            style: dynamicWidth
              ? {
                  width: dynamicWidth,
                  minWidth: dynamicWidth,
                  maxWidth: dynamicWidth,
                }
              : {width: menuWidth, maxWidth: menuWidth},
          },
          anchorOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'bottom', horizontal: 'left'}
              : {vertical: 'top', horizontal: 'left'},
          transformOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'top', horizontal: 'left'}
              : {vertical: 'bottom', horizontal: 'left'},
          MenuListProps: {autoFocusItem: false},
        }}
        data-testid="arr-multi-select-dropdown"
      >
        {/* Search Bar */}
        {searchable && (
          <Box
            className="arr-dropdown-search"
            data-testid="arr-dropdown-search"
            // prevent clicks inside search box from closing dropdown
            onClick={(e) => e.stopPropagation()}
          >
            <ArrTextField
              fullWidth
              id="dropdown-search"
              state="enabled"
              placeholder="Search"
              dataTestId={
                useDataTestIdsSimilarToArrMultiSelect
                  ? `multi-select-dd-${searchFieldText.split('')[0]}`
                  : 'multi-select-lazy-search-text'
              }
              sizeVariant="medium"
              value={searchFieldText}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchFieldText(e.target.value)
              }
              inputRef={searchInputRef}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      // keep dropdown open and move focus to first menu item on next frame
                      setIsDropdownOpen(true);

                      requestAnimationFrame(() => {
                        if (searchFieldText) {
                          (
                            document.querySelector(
                              '[data-testid="clear-button"]'
                            ) as HTMLElement
                          )?.focus();

                          return;
                        }

                        const container = scrollContainerRef.current;
                        if (!container) return;
                        const first = container.querySelector<HTMLElement>(
                          'li[role="option"], [role="menuitem"], button, [tabindex]:not([tabindex="-1"])'
                        );
                        if (first) first.focus();
                      });
                    }
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchFieldText ? (
                        <ArrIconButton
                          icon={<ClearIcon />}
                          onClick={() => setSearchFieldText('')}
                          onKeyDown={(e) => {
                            if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              requestAnimationFrame(() => {
                                const container = scrollContainerRef.current;
                                if (!container) return;
                                const first =
                                  container.querySelector<HTMLElement>(
                                    'li[role="option"], [role="menuitem"], button, [tabindex]:not([tabindex="-1"])'
                                  );
                                if (first) first.focus();
                              });
                            }
                            if (e.shiftKey && e.key === 'Tab') {
                              e.preventDefault();
                              e.stopPropagation();
                              searchInputRef.current?.focus();
                            }
                            if (e.key === 'Enter' || e.code === 'Space') {
                              e.preventDefault();
                              setSearchFieldText('');
                              searchInputRef.current?.focus();
                            }
                          }}
                          ariaLabel="clear"
                          shape="none"
                          size="small"
                          tabIndex={0}
                          data-testid="clear-button"
                          testId={
                            useDataTestIdsSimilarToArrMultiSelect
                              ? 'arr-multi-select-dropdown-clear-icon-button'
                              : 'arr-multi-select-lazy-load-dropdown-clear-icon-button'
                          }
                        />
                      ) : (
                        <ArrIconButton
                          icon={<SearchIcon />}
                          onClick={() => {}}
                          ariaLabel="search"
                          shape="none"
                          size="small"
                          data-testid="search-button"
                          testId={
                            useDataTestIdsSimilarToArrMultiSelect
                              ? 'arr-multi-select-dropdown-search-icon-button'
                              : 'arr-multi-select-lazy-load-dropdown-search-icon-button'
                          }
                        />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Divider className="divider-dropdown" />
          </Box>
        )}

        {/* Dropdown List */}
        <Box
          ref={scrollContainerRef}
          style={{maxHeight: menuMaxHeight}}
          onScroll={handleScroll}
          className={'arr-dropdown-scroll lazy-load-dropdown-list'}
          onKeyDown={(e) => {
            if (e.shiftKey && e.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              requestAnimationFrame(() => {
                searchInputRef.current?.focus();
              });
            }
          }}
        >
          {filteredItems.map((item, index) => {
            const key = String(item[dropdownItemKey]);
            const label = item[dropdownItemDisplay];
            const isSelected = selectedItem.some(
              (s) => s[dropdownItemKey] === item[dropdownItemKey]
            );

            return (
              <MenuItem
                key={key}
                value={key}
                onClick={() => handleItemClick(item)}
                tabIndex={0}
                data-testid={
                  useDataTestIdsSimilarToArrMultiSelect
                    ? `arr-multi-select-dropdown-menu-item-${checkboxDataTestId}`
                    : ''
                }
              >
                <Checkbox
                  data-testid={
                    useDataTestIdsSimilarToArrMultiSelect
                      ? `${checkboxDataTestId}-option-${key}`
                      : `${checkboxDataTestId}-option-${index}`
                  }
                  className="arr-dropdown-checkbox"
                  checked={isSelected}
                  size="small"
                  icon={<UnCheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                />
                <ArrTooltip content={label} showOnTruncate>
                  <Typography
                    className="arr-dropdown-option"
                    data-testid={
                      useDataTestIdsSimilarToArrMultiSelect
                        ? `arr-multi-select-dropdown-label-${dataTestId}-${index}`
                        : `arr-multi-select-lazy-load-menu-item-${dataTestId}-label-${index}`
                    }
                  >
                    {label}
                  </Typography>
                </ArrTooltip>
              </MenuItem>
            );
          })}

          {/* Lazy Loading Loader */}
          {isLoadingMore && (
            <Box className="lazy-loader-no-data">
              <ArrLoadingSpinner size={32} />
            </Box>
          )}

          {/* No Data */}
          {!filteredItems.length && !isLoadingMore && (
            <Box className="lazy-loader-no-data">
              <Typography
                className="arr-dropdown-option"
                data-testid={`arr-multi-select-lazy-load-dropdown-no-result-${dataTestId}`}
              >
                {noResultText === '' ? t('common.noResult') : noResultText}
              </Typography>
            </Box>
          )}
        </Box>
      </Select>
    </FormControl>
  );
};

export default ArrMultiSelectLazyLoadDropdown;
